import { Component } from "@theme/component";
import { QuantitySelectorUpdateEvent } from "@theme/events";
import { fetchConfig, createCartMessageHandler, getCart, isBuyNowCartSyncSource, shouldSkipBuyNowCartSync } from "@theme/utilities";
import { ThemeEvents, VariantUpdateEvent, CartUpdateEvent } from "@theme/events";

/**
 * Custom quantity selector for product page that shows delete button when qty=1 and minus button when qty>1
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} deleteMinusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
class ProductQuantitySelectorComponent extends Component {
  requiredRefs = ["quantityInput", "deleteMinusButton", "plusButton"];
  #abortController = new AbortController();
  #pendingCartUpdate = null;
  #isUpdatingCart = false;
  /** @type {(() => void) | null} */
  #cartMessageCleanup = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    // Listen for cart updates to sync quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Listen for cart quantity change events from cart drawer using shared utility
    // Use a getter function to dynamically get variant ID (supports variant changes)
    const { cleanup: cleanupCartMessage } = createCartMessageHandler({
      variantId: () => this.dataset.variantId || null,
      onVariantQuantityChange: this.#onVariantQuantityChange,
      logPrefix: "Product quantity selector",
    });
    this.#cartMessageCleanup = cleanupCartMessage;

    // Listen for variant updates to sync variant ID and constraints
    const closestSection = this.closest("section");
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    }

    // Listen for quantity changes from other selectors to sync bidirectionally
    document.addEventListener("product-quantity-change", this.#onQuantityChangeFromOther, { signal });

    // Listen for form submit to ensure hidden inputs don't cause validation errors
    const form = this.closest("form");
    if (form) {
      form.addEventListener("submit", this.#onFormSubmit, { signal });
    }

    this.updateButtonIcon();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#pendingCartUpdate) {
      clearTimeout(this.#pendingCartUpdate);
    }
    if (this.#cartMessageCleanup) {
      this.#cartMessageCleanup();
    }
    this.#abortController.abort();
  }

  /**
   * Handles variant quantity changes from cart drawer message events.
   * Called by the shared cart message handler utility.
   * @param {Object} data - The variant quantity change data
   * @param {string|number} data.variantId - The variant ID
   * @param {number} data.quantity - The new quantity for this variant
   */
  #onVariantQuantityChange = async ({ variantId, quantity, total_cart_quantity }) => {
    //console.log("Product quantity selector - Updating cart quantity to:", quantity);
    this.setCartQuantity(quantity);

    // When quantity becomes 0, dispatch cart update event to update buy buttons UI
    // This ensures ATC button shows and quantity selector hides
    if (quantity === 0) {
      const itemCount = typeof total_cart_quantity === "number" ? total_cart_quantity : 0;
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.cartUpdate, {
          detail: {
            sourceId: this.id,
            data: {
              source: "cart-drawer-message",
              itemCount: itemCount,
            },
          },
        })
      );
    }
  };

  /**
   * Handles cart update events
   * @param {Event} event
   */
  #onCartUpdate = async (event) => {
    if (shouldSkipBuyNowCartSync(event)) return;
    // Skip optimistic events — real data comes on confirmation
    if (event.detail?.data?.isOptimistic) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    // Skip if this event came from this component to avoid loops
    const eventSourceId = event.detail?.sourceId;
    const eventSource = event.detail?.data?.source;

    // Skip if event came from this component or if we're currently updating the cart
    if (eventSourceId === this.id || eventSource === "product-quantity-selector" || this.#isUpdatingCart) {
      return;
    }

    try {
      const cart = await getCart({ forceRefresh: isBuyNowCartSyncSource(eventSource) });

      const cartItem = cart.items.find((/** @type {any} */ item) => item.variant_id.toString() === variantId.toString());
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Only update if the cart quantity is different from what we have
      const currentCartQty = parseInt(this.dataset.cartQuantity || "0");
      const { quantityInput } = this.refs;
      const currentValue = parseInt(quantityInput?.value || "0");

      // Don't update if:
      // 1. Cart quantity hasn't changed, OR
      // 2. Current value already matches the new cart quantity (optimistic update was correct)
      // Buy Now restore must still call setCartQuantity so hidden wrappers get data-cart-quantity.
      if (isBuyNowCartSyncSource(eventSource) || (currentCartQty !== cartQty && currentValue !== cartQty)) {
        this.setCartQuantity(cartQty);
      }
    } catch (error) {
      console.error("Failed to fetch cart quantity:", error);
    }
  };

  /**
   * Handles quantity changes from other selectors (bidirectional sync)
   * @param {CustomEvent} event
   */
  #onQuantityChangeFromOther = (event) => {
    // Don't sync if this event came from this component
    if (event.detail.source === this) return;

    const variantId = this.dataset.variantId;
    if (!variantId || variantId.toString() !== event.detail.variantId.toString()) return;

    // Update this selector's value to match
    const { quantityInput } = this.refs;
    const newValue = event.detail.quantity;

    if (quantityInput && parseInt(quantityInput.value) !== newValue) {
      quantityInput.value = newValue.toString();
      this.updateButtonIcon();
    }
  };

  /**
   * Handles variant update events
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    // Check if this update is for the correct product
    const productFormComponent = this.closest("product-form-component");
    if (productFormComponent) {
      const productId = productFormComponent.dataset.productId;
      const eventProductId = event.detail.data.productId;

      // Skip if this is for a different product (unless it's a new product)
      if (eventProductId && productId && eventProductId !== productId && !event.detail.data.newProduct) {
        return;
      }
    }

    const newVariantId = event.detail.resource?.id;
    if (!newVariantId) return;

    const isAvailable = event.detail.resource != null && event.detail.resource.available !== false;

    // Get the new quantity input and component from the updated HTML to extract constraints and digital flag
    const newSelectorEl = event.detail.data.html?.querySelector("product-quantity-selector-component");
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (event.detail.data.html?.querySelector('product-quantity-selector-component input[ref="quantityInput"]'));
    const isDigitalProduct = newSelectorEl?.getAttribute("data-is-digital-product") === "true";

    if (newQuantityInput) {
      // Update variant ID, constraints, and digital-product disabled state
      this.updateVariant(newVariantId.toString(), newQuantityInput.min || "1", newQuantityInput.max || null, newQuantityInput.step || "1", isDigitalProduct);
    } else {
      // Fallback: just update variant ID if we can't find the new input
      this.dataset.variantId = newVariantId.toString();
    }

    // Sold out variant: clear local cart qty so quantity UI cannot linger from previous variant
    if (!isAvailable) {
      this.setCartQuantity(0);
    }

    // Note: Cart quantity update is handled by product-form-component via #fetchAndUpdateCartQuantity()
  };

  /**
   * Updates variant ID, quantity constraints, and digital-product disabled state
   * @param {string} variantId - The new variant ID
   * @param {string} min - Minimum quantity
   * @param {string | null} max - Maximum quantity (null if no max)
   * @param {string} step - Quantity step/increment
   * @param {boolean} [isDigitalProduct] - Whether the variant is digital (quantity input and plus disabled)
   */
  updateVariant(variantId, min, max, step, isDigitalProduct) {
    // Update variant ID
    this.dataset.variantId = variantId;

    // Update digital-product state so quantity selector reflects disabled state on variant change
    const isDigital = isDigitalProduct === true;
    this.dataset.isDigitalProduct = isDigital ? "true" : "false";

    const { quantityInput, plusButton } = this.refs;
    const cartQty = parseInt(this.dataset.cartQuantity || "0");

    if (quantityInput) {
      quantityInput.min = min;
      quantityInput.step = step;
      if (max) {
        quantityInput.max = max;
      } else {
        quantityInput.removeAttribute("max");
      }

      // Update value to respect new min if current value is below min
      const currentValue = parseInt(quantityInput.value) || 0;
      const minValue = parseInt(min) || 1;
      if (currentValue < minValue) {
        quantityInput.value = cartQty > 0 ? cartQty.toString() : minValue.toString();
      }

      // Ensure value respects max if it exists
      if (max) {
        const maxValue = parseInt(max);
        const currentValueAfter = parseInt(quantityInput.value) || 0;
        if (currentValueAfter > maxValue) {
          quantityInput.value = maxValue.toString();
        }
      }

      // Digital products: input always disabled; non-digital: disabled only when cart qty is 0
      quantityInput.disabled = isDigital || cartQty === 0;
      if (quantityInput.disabled) {
        quantityInput.removeAttribute("name");
      } else {
        quantityInput.setAttribute("name", "quantity");
      }
    }

    if (plusButton) {
      plusButton.disabled = isDigital;
      if (isDigital) {
        plusButton.setAttribute("aria-busy", "true");
      } else {
        plusButton.removeAttribute("aria-busy");
      }
    }

    this.updateButtonIcon();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    const currentValue = parseInt(quantityInput.value) || 0;
    const currentCartQty = parseInt(this.dataset.cartQuantity || "0");
    const expectedValue = cartQty > 0 ? cartQty : 0;

    // Skip update if both cart quantity and displayed value are already correct
    // This prevents flickering when the value is already correct
    if (currentCartQty === cartQty && currentValue === expectedValue) {
      // Still update data attribute and button icon in case they're out of sync
      quantityInput.setAttribute("data-cart-quantity", cartQty.toString());
      this.updateButtonIcon();
      return;
    }

    // If we're currently updating the cart, preserve the optimistic value
    // The input value is already set optimistically by the user's click
    // We should NOT change it during the cart update to prevent flicker
    if (this.#isUpdatingCart) {
      // During cart update, preserve the optimistic value the user set
      // Only update data attributes to reflect the new cart quantity
      // The input value stays at what the user set it to (optimistic update)
      this.dataset.cartQuantity = cartQty.toString();
      quantityInput.setAttribute("data-cart-quantity", cartQty.toString());

      // Update button icon to reflect the current input value (not cart quantity)
      // This ensures the UI matches what the user sees
      this.updateButtonIcon();
      return;
    }

    this.dataset.cartQuantity = cartQty.toString();
    quantityInput.setAttribute("data-cart-quantity", cartQty.toString());

    // Check if wrapper is hidden - use multiple methods to detect hidden state
    const wrapper = this.closest('[ref="productQuantitySelectorWrapper"]');
    let isWrapperHidden = false;
    if (wrapper instanceof HTMLElement) {
      // Check for hidden attribute, hidden class, or computed display style
      const computedStyle = window.getComputedStyle(wrapper);
      isWrapperHidden = wrapper.hidden || wrapper.classList.contains("hidden") || wrapper.style.display === "none" || computedStyle.display === "none" || computedStyle.visibility === "hidden" || wrapper.offsetParent === null; // offsetParent is null when element is hidden
    }

    // Only update input value if it's different to prevent unnecessary updates
    // This is critical - don't update if the user just set it to this value
    if (currentValue !== expectedValue && !(cartQty === 0 && !isWrapperHidden)) {
      quantityInput.value = expectedValue.toString();
    }

    // Add/remove name attribute to include/exclude from form validation
    // Remove name if cartQty is 0 OR if wrapper is hidden (prevents "not focusable" error) OR if digital product
    const isDigitalProduct = this.dataset.isDigitalProduct === "true";
    if (cartQty === 0 || isWrapperHidden || isDigitalProduct) {
      quantityInput.removeAttribute("name");
      quantityInput.disabled = true;
    } else {
      quantityInput.setAttribute("name", "quantity");
      quantityInput.disabled = false;
    }

    // Keep plus button disabled for digital products
    if (this.refs.plusButton) {
      this.refs.plusButton.disabled = isDigitalProduct;
    }

    this.updateButtonIcon();
  }

  /**
   * Handles form submit to ensure hidden inputs don't cause validation errors
   * @param {Event} event
   */
  #onFormSubmit = (event) => {
    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    // Check if wrapper is hidden before form submission
    const wrapper = this.closest('[ref="productQuantitySelectorWrapper"]');
    let isWrapperHidden = false;
    if (wrapper instanceof HTMLElement) {
      const computedStyle = window.getComputedStyle(wrapper);
      isWrapperHidden = wrapper.hidden || wrapper.classList.contains("hidden") || wrapper.style.display === "none" || computedStyle.display === "none" || computedStyle.visibility === "hidden" || wrapper.offsetParent === null;
    }

    // Remove name attribute if wrapper is hidden to prevent validation errors
    if (isWrapperHidden && quantityInput.hasAttribute("name")) {
      quantityInput.removeAttribute("name");
    }
  };

  /**
   * Gets current values from DOM
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;
    return {
      min: parseInt(quantityInput.min) || 1,
      max: quantityInput.max ? parseInt(quantityInput.max) : null,
      step: parseInt(quantityInput.step) || 1,
      value: parseInt(quantityInput.value) || 0,
      cartQuantity: parseInt(quantityInput.getAttribute("data-cart-quantity") || "0"),
    };
  }

  /**
   * Updates the button icon based on current quantity
   */
  updateButtonIcon() {
    const { deleteMinusButton, quantityInput } = this.refs;
    if (!deleteMinusButton || !quantityInput) return;

    const currentValue = parseInt(quantityInput.value) || 0;
    const deleteIcon = deleteMinusButton.querySelector(".quantity-icon--delete");
    const minusIcon = deleteMinusButton.querySelector(".quantity-icon--minus");

    if (currentValue === 1) {
      // Show delete icon only, hide minus icon completely
      if (deleteIcon) {
        deleteIcon.removeAttribute("hidden");
      }
      if (minusIcon) {
        minusIcon.setAttribute("hidden", "");
      }
    } else if (currentValue > 1) {
      // Show minus icon only, hide delete icon completely
      if (deleteIcon) {
        deleteIcon.setAttribute("hidden", "");
      }
      if (minusIcon) {
        minusIcon.removeAttribute("hidden");
      }
    }

    // Show/hide the button based on quantity
    if (currentValue === 0) {
      deleteMinusButton.setAttribute("hidden", "");
    } else {
      deleteMinusButton.removeAttribute("hidden");
    }
  }

  /**
   * Enables or disables the quantity control buttons
   * @param {boolean} disabled - Whether to disable the buttons
   */
  #setButtonsDisabled(disabled) {
    const { deleteMinusButton, plusButton } = this.refs;

    if (deleteMinusButton) {
      deleteMinusButton.disabled = disabled;
      if (disabled) {
        deleteMinusButton.setAttribute("aria-busy", "true");
      } else {
        deleteMinusButton.removeAttribute("aria-busy");
      }
    }

    if (plusButton) {
      plusButton.disabled = disabled;
      if (disabled) {
        plusButton.setAttribute("aria-busy", "true");
      } else {
        plusButton.removeAttribute("aria-busy");
      }
    }
  }

  /**
   * Shows the loader if it exists (only on PDP)
   */
  #showLoader() {
    const wrapper = this.closest('[ref="productQuantitySelectorWrapper"]');
    if (!wrapper) return;

    const loader = wrapper.querySelector('[ref="quantitySelectorLoader"]');
    if (loader && !wrapper.classList.contains("hidden")) {
      loader.classList.remove("hidden");
    }
  }

  /**
   * Hides the loader if it exists
   */
  #hideLoader() {
    const wrapper = this.closest('[ref="productQuantitySelectorWrapper"]');
    if (!wrapper) return;

    const loader = wrapper.querySelector('[ref="quantitySelectorLoader"]');
    if (loader) {
      loader.classList.add("hidden");
    }
  }

  /**
   * Handles delete/minus button click
   * @param {Event} event
   */
  handleDeleteMinus(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    // Prevent action if cart update is in progress
    if (this.#isUpdatingCart) {
      return;
    }

    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    if (currentValue === 1) {
      // Delete: Remove item from cart
      this.removeFromCart();
    } else if (currentValue > 1) {
      // Decrease quantity
      this.decreaseQuantity(event);
    }
  }

  /**
   * Removes item from cart
   */
  async removeFromCart() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    // Prevent multiple simultaneous cart updates
    if (this.#isUpdatingCart) {
      return;
    }

    this.#isUpdatingCart = true;

    // Disable buttons during cart update
    this.#setButtonsDisabled(true);

    // Show loader on PDP
    this.#showLoader();

    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    try {
      const response = await fetch("/cart/change.js", {
        ...fetchConfig(),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 0,
        }),
      });

      // Parse response first to check for errors
      let cart;
      try {
        cart = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, check response status
        if (!response.ok) {
          throw new Error(`Failed to remove item from cart: ${response.status} ${response.statusText}`);
        }
        throw new Error("Failed to parse cart response");
      }

      // Check for errors in the response (Shopify cart API can return errors with 200 status)
      if (cart.errors || cart.status === "error" || (cart.status && cart.status !== 200)) {
        const errorMessage = cart.message || cart.description || "Failed to remove item from cart";
        console.error("Cart removal error:", errorMessage, cart.errors);
        throw new Error(errorMessage);
      }

      // Check HTTP status code
      if (!response.ok) {
        const errorMessage = cart?.message || `Failed to remove item from cart: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
      }

      // Update local state - preserve the optimistic value (should be 1 or 0)
      // Don't reset it to min during the update
      this.setCartQuantity(0);

      // Dispatch quantity change event for syncing AFTER cart update completes
      if (variantId) {
        requestAnimationFrame(() => {
          document.dispatchEvent(
            new CustomEvent("product-quantity-change", {
              detail: {
                variantId: variantId.toString(),
                quantity: 0,
                source: this,
              },
              bubbles: true,
            })
          );
        });
      }

      // Dispatch cart update event after a delay
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent(ThemeEvents.cartUpdate, {
            detail: {
              sourceId: this.id,
              data: {
                source: "product-quantity-selector",
                itemCount: cart.item_count,
              },
            },
          })
        );
        // Reset flag and re-enable buttons after event is dispatched
        setTimeout(() => {
          this.#isUpdatingCart = false;
          this.#setButtonsDisabled(false);
          this.#hideLoader();
        }, 150);
      }, 150);

      // Show Add to Cart button FIRST, then hide quantity selector to prevent white space
      // This ensures smooth transition without gap - do synchronously but in correct order
      const productFormComponent = this.closest("product-form-component");
      const wrapper = this.closest('[ref="productQuantitySelectorWrapper"]');

      // Handle both main form (buy-buttons) and sticky bar
      if (productFormComponent) {
        // Check if this is in sticky bar or main form
        const isStickyBar = productFormComponent.closest("product-sticky-bar");

        if (isStickyBar) {
          // Handle sticky bar: find ATC wrapper in the same form component
          // This works for both desktop and mobile since they're in the same product-form-component
          const productFormButtons = productFormComponent.querySelector('[ref="productFormButtons"]') || productFormComponent.querySelector(".product-sticky-bar__buttons") || productFormComponent.querySelector(".product-sticky-bar__mobile-actions");

          // Find ATC wrapper in the same container as the quantity wrapper
          const stickyAtcWrapper = productFormButtons?.querySelector('[ref="addToCartButtonWrapper"]') || productFormComponent.querySelector('[ref="addToCartButtonWrapper"]');

          if (stickyAtcWrapper instanceof HTMLElement) {
            // Show ATC button first (synchronously)
            stickyAtcWrapper.classList.remove("hidden");
            stickyAtcWrapper.hidden = false;

            // Then immediately hide quantity selector (synchronously)
            if (wrapper instanceof HTMLElement) {
              wrapper.classList.add("hidden");
              wrapper.hidden = true;
            }
          } else if (wrapper instanceof HTMLElement) {
            // Fallback: hide quantity selector if ATC wrapper not found
            wrapper.classList.add("hidden");
            wrapper.hidden = true;
          }
        } else {
          // Handle main form (buy-buttons)
          const productFormButtons = productFormComponent.querySelector('[ref="productFormButtons"]');
          const addToCartWrapper = productFormButtons?.querySelector('[ref="addToCartButtonWrapper"]');

          if (addToCartWrapper instanceof HTMLElement) {
            // Show ATC button first (synchronously)
            addToCartWrapper.classList.remove("hidden");
            addToCartWrapper.hidden = false;

            // Then immediately hide quantity selector (synchronously)
            // Browser will batch these DOM updates, so no white space should appear
            if (wrapper instanceof HTMLElement) {
              wrapper.classList.add("hidden");
              wrapper.hidden = true;
            }
          } else if (wrapper instanceof HTMLElement) {
            // Fallback: hide quantity selector if ATC wrapper not found
            wrapper.classList.add("hidden");
            wrapper.hidden = true;
          }
        }
      } else if (wrapper instanceof HTMLElement) {
        // Fallback: hide quantity selector if product form component not found
        wrapper.classList.add("hidden");
        wrapper.hidden = true;
      }
    } catch (error) {
      console.error("Failed to remove item from cart:", error);

      // Reset state and re-enable buttons
      this.#isUpdatingCart = false;
      this.#setButtonsDisabled(false);
      this.#hideLoader();

      // Optionally dispatch an error event for UI feedback
      document.dispatchEvent(
        new CustomEvent("product-quantity-error", {
          detail: {
            variantId: variantId?.toString(),
            error: error.message || "Failed to remove item from cart",
            source: this,
          },
          bubbles: true,
        })
      );
    }
  }

  /**
   * Decreases quantity
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();

    const newValue = Math.max(min, value - step);

    // Update input value optimistically
    quantityInput.value = newValue.toString();

    // Update button icon immediately to reflect the new value
    this.updateButtonIcon();

    // Then trigger cart update
    this.onQuantityChange();
  }

  /**
   * Increases quantity
   * @param {Event} event
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    // Prevent action if cart update is in progress
    if (this.#isUpdatingCart) {
      return;
    }

    const { quantityInput } = this.refs;
    const { max, step, value } = this.getCurrentValues();

    const newValue = max !== null ? Math.min(max, value + step) : value + step;
    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonIcon();
  }

  /**
   * Handles quantity change event
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);
    const cartQuantity = parseInt(quantityInput.getAttribute("data-cart-quantity") || "0");

    // Calculate quantity to add/update
    const quantityToAdd = newValue - cartQuantity;

    if (quantityToAdd === 0) return;

    // Don't dispatch quantity change event here - wait until cart update completes
    // This prevents other components from trying to sync before the cart is actually updated
    // The event will be dispatched in updateCart() after the cart update succeeds

    // Update cart
    this.updateCart(quantityToAdd);
    this.updateButtonIcon();
  }

  /**
   * Updates cart with new quantity
   * @param {number} quantityChange - The change in quantity (positive or negative)
   */
  async updateCart(quantityChange) {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    // Prevent multiple simultaneous cart updates
    if (this.#isUpdatingCart) {
      return;
    }

    this.#isUpdatingCart = true;

    // Disable buttons during cart update
    this.#setButtonsDisabled(true);

    // Show loader on PDP
    this.#showLoader();

    const { quantityInput } = this.refs;
    const { min, max } = this.getCurrentValues();
    const currentValue = parseInt(quantityInput.value) || 0;

    // Validate quantity before making API call
    if (currentValue < min) {
      console.error(`Quantity ${currentValue} is below minimum ${min}`);
      this.#isUpdatingCart = false;
      this.#setButtonsDisabled(false);
      this.#hideLoader();
      quantityInput.value = min.toString();
      this.updateButtonIcon();
      return;
    }

    if (max !== null && currentValue > max) {
      console.error(`Quantity ${currentValue} exceeds maximum ${max}`);
      this.#isUpdatingCart = false;
      this.#setButtonsDisabled(false);
      this.#hideLoader();
      quantityInput.value = max.toString();
      this.updateButtonIcon();
      return;
    }

    try {
      const response = await fetch("/cart/change.js", {
        ...fetchConfig(),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: variantId,
          quantity: currentValue,
        }),
      });

      // Parse response first to check for errors
      let cart;
      try {
        cart = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, check response status
        if (!response.ok) {
          throw new Error(`Failed to update cart: ${response.status} ${response.statusText}`);
        }
        throw new Error("Failed to parse cart response");
      }

      // Check for errors in the response (Shopify cart API can return errors with 200 status)
      if (cart.errors || cart.status === "error" || (cart.status && cart.status !== 200)) {
        const errorMessage = cart.message || cart.description || "Failed to update cart";
        console.error("Cart update error:", errorMessage, cart.errors);

        // Revert optimistic update
        const previousCartQty = parseInt(quantityInput.getAttribute("data-cart-quantity") || "0");
        const min = parseInt(quantityInput.min) || 1;
        quantityInput.value = previousCartQty > 0 ? previousCartQty.toString() : min.toString();
        this.updateButtonIcon();

        throw new Error(errorMessage);
      }

      // Check HTTP status code
      if (!response.ok) {
        const errorMessage = cart?.message || `Failed to update cart: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
      }

      const cartItem = cart.items?.find((/** @type {any} */ item) => item.variant_id.toString() === variantId.toString());
      const newCartQty = cartItem ? cartItem.quantity : 0;

      // Update data-cart-quantity (this will skip if value hasn't changed)
      // Don't update the input value if we're in the middle of an update and the value is already correct
      this.setCartQuantity(newCartQty);

      // Dispatch quantity change event for syncing AFTER cart update completes
      // This ensures other components sync with the actual cart state, not optimistic values
      if (variantId) {
        // Use requestAnimationFrame to ensure DOM updates are complete before dispatching
        requestAnimationFrame(() => {
          document.dispatchEvent(
            new CustomEvent("product-quantity-change", {
              detail: {
                variantId: variantId.toString(),
                quantity: newCartQty,
                source: this,
              },
              bubbles: true,
            })
          );
        });
      }

      // Dispatch cart update event after a delay to ensure setCartQuantity completes
      // Set flag to false after a longer delay to allow other components to sync
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent(ThemeEvents.cartUpdate, {
            detail: {
              sourceId: this.id,
              data: {
                source: "product-quantity-selector",
                itemCount: cart.item_count,
              },
            },
          })
        );
        // Reset flag and re-enable buttons after event is dispatched
        setTimeout(() => {
          this.#isUpdatingCart = false;
          this.#setButtonsDisabled(false);
          this.#hideLoader();
        }, 150);
      }, 150);
    } catch (error) {
      console.error("Failed to update cart:", error);

      // Revert optimistic update on error
      const { quantityInput } = this.refs;
      if (quantityInput) {
        const previousCartQty = parseInt(quantityInput.getAttribute("data-cart-quantity") || "0");
        const min = parseInt(quantityInput.min) || 1;
        const revertValue = previousCartQty > 0 ? previousCartQty : min;

        // Only revert if current value doesn't match previous cart quantity
        if (parseInt(quantityInput.value) !== revertValue) {
          quantityInput.value = revertValue.toString();
          this.updateButtonIcon();
        }
      }

      // Reset state and re-enable buttons
      this.#isUpdatingCart = false;
      this.#setButtonsDisabled(false);
      this.#hideLoader();

      // Optionally dispatch an error event for UI feedback
      // You can listen to this event to show error messages to users
      document.dispatchEvent(
        new CustomEvent("product-quantity-error", {
          detail: {
            variantId: variantId?.toString(),
            error: error.message || "Failed to update cart",
            source: this,
          },
          bubbles: true,
        })
      );
    }
  }

  /**
   * Handles the quantity set event (on blur)
   * @param {Event} event
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();

    const { quantityInput } = this.refs;
    const { min, step, max } = this.getCurrentValues();

    // Snap to bounds
    let quantity = Math.max(min, parseInt(event.target.value) || min);
    if (max !== null) {
      quantity = Math.min(max, quantity);
    }

    // Validate step increment
    if ((quantity - min) % step !== 0) {
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonIcon();
  }

  /**
   * When input gets focused, select the value
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;
    quantityInput.select();
  }
}

if (!customElements.get("product-quantity-selector-component")) {
  customElements.define("product-quantity-selector-component", ProductQuantitySelectorComponent);
}
